
import React from "react";
import Loading from "./Loading";
import { i18nManager } from "../i18n";
import { Scheme } from "@rivtn/noseur";
import { Schemes, Utils } from "../utils";
import MobileCompound from "./mobile/Compound";
import DesktopCompound from "./desktop/Compound";
import { ViewMode } from "../constants/ViewMode";
import { Subscriber } from "../utils/Subscriber";
import ErrorSuccessView from "./ErrorSuccessView";
import * as ReactRouterDom from "react-router-dom";
import { PaymentMethod } from "../constants/PaymentMethod";
import { ITransaction, PloutousService, SanitizedResponse } from "../services";

function Landing() {

    const labels = i18nManager.Labels;
    const ploutousService = PloutousService.getInstance();
    const paymentMethodState = React.useState<PaymentMethod>();
    const [transaction, setTransaction] = React.useState<ITransaction>();
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [viewMode, setViewMode] = React.useState<ViewMode>(ViewMode.LOADING);
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const [language, setLanguage] = React.useState(searchParams.get("lang") ?? "en_UK");
    const [country, setCountry] = React.useState(searchParams.get("country") ?? undefined);

    React.useEffect(() => {
        Subscriber.subscribe("UPDATED_TRANSACTION_OBJECT", (transaction: ITransaction) => {
            setTransaction(transaction);
        });
        return () => {
            Subscriber.clearSubscriptions("UPDATED_TRANSACTION_OBJECT");
        };
    }, []);

    React.useEffect(() => {
        const errors: string[] = [];
        if (!searchParams.has("bak")) errors.push(labels.error.missing_bak);
        if (!searchParams.has("params")) errors.push(labels.error.missing_params);
        if (errors.length) {
            setErrorMessages(errors);
            setViewMode(ViewMode.ERROR);
            return;
        }
        fetchTransaction();
    }, [country]);

    if (viewMode === ViewMode.LOADING) {
        return (<Loading />);
    } else if (!transaction || viewMode === ViewMode.ERROR) {
        return (<ErrorSuccessView transaction={transaction} redirectUrl={transaction?.redirects?.failure} messages={errorMessages} actions={[
            ...(transaction ? [{
                raised: true,
                scheme: Scheme.DANGER,
                text: i18nManager.Labels.common.back_to_business,
                onClick: () => {
                    window.location.replace(transaction.redirects.failure);
                },
            }] : []),
            {
                raised: true,
                scheme: Scheme.DANGER,
                text: labels.common.back,
                onClick: () => window.history.back(),
            }
        ]} />);
    } else if (transaction && !paymentMethodState[0] && (transaction.status >= 119 && transaction.status <= 129)) {
        return (<ErrorSuccessView type="WARNING" transaction={transaction} redirectUrl={transaction?.redirects?.success} messages={[i18nManager.Labels.common.partially_paid_cannot_accept_payment]} actions={[{
            raised: true,
            scheme: Schemes.RIVTN_PLOUTOUS,
            text: i18nManager.Labels.common.back_to_business,
            onClick: () => {
                window.location.replace(transaction.redirects.success);
            },
        }]} />);
    } else if (transaction && !paymentMethodState[0] && (transaction.amount === 0 || Utils.paymentFullyPaid(transaction.status))) {
        return (<ErrorSuccessView type="SUCCESS" transaction={transaction} redirectUrl={transaction?.redirects?.success} messages={[i18nManager.Labels.common.fully_paid]} actions={[{
            raised: true,
            scheme: Schemes.RIVTN_PLOUTOUS,
            text: i18nManager.Labels.common.back_to_business,
            onClick: () => {
                window.location.replace(transaction.redirects.success);
            },
        }]} />);
    }

    const payload = {
        language,
        transaction,
        fetchTransaction,
        paymentMethodState,
        businessId: searchParams.get("bak")!,
        switchTransactionCountry: setCountry,
    };

    return (<>
        {viewMode === ViewMode.MOBILE && (<MobileCompound {...payload} />)}
        {viewMode === ViewMode.DESKTOP && (<DesktopCompound {...payload} />)}
    </>); // use resize sensor from noseur

    async function fetchTransaction(queryOnly?: boolean, onDone?: (result?: ITransaction, error?: any) => void) {
        if (!queryOnly) setViewMode(ViewMode.LOADING);
        ploutousService.getTransaction(searchParams.get("params")!, searchParams.get("bak")!, country, searchParams.get("ib")!).then(({ sanitized }: SanitizedResponse<ITransaction>) => {
            if (queryOnly) {
                onDone?.(sanitized, undefined);
                return;
            }
            setTransaction(sanitized);
            if (!searchParams.has("lang")) {
                setLanguage(sanitized.language_code ?? "en_UK");
            }
            if ((new Date()).getTime() > (new Date(sanitized.expiry_date)).getTime()) {
                throw new Error(labels.error.txn_expired);
            } else if (!sanitized.payment_options?.length) {
                throw new Error(labels.error.no_payment_methods);
            }
            // set payment method to first in list
            if (!(sanitized.amount === 0)) {
                paymentMethodState[1](sanitized.payment_options.find((p, i) => ((!sanitized.configuration.default_payment_method && i === 0) || p.method === sanitized.configuration.default_payment_method))?.method);
            }
            setViewMode(Utils.SCREEN_DIMENSION.width > 720 ? ViewMode.DESKTOP : ViewMode.MOBILE);
            onDone?.(sanitized, undefined);
        }).catch((error: any) => {
            if (queryOnly) {
                onDone?.(undefined, error);
                return;
            }
            PloutousService.reportError(error);
            setErrorMessages(error.errors ?? [error.message]);
            setViewMode(ViewMode.ERROR);
        });
    }

}

export default Landing;
